import { Box } from '@/components/Box/Box';
import { NotificationMessage } from '@/components/Notification/styles';
import { ValueOf } from '@/types/sharedTypes';

export const PAYMENT_TYPE_DB = {
  INTERNATIONAL: 'international',
  DOMESTIC: 'domestic',
} as const;

export const DEPOSIT_ACCOUNT_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'in_active',
};

export const REDIRECT_URL_KEY = 'REDIRECT_URL';
export const CURRENT_ROLE_KEY = 'CURRENT_ROLE';

export const PIN_TOKEN_EVENT_SCOPE = {
  CREATE_OPCO_USER: 'create_opco_user',
  BLOCK_OPCO_USER: 'block_opco_user',
  RESET_OPCO_USER: 'reset_opco_user',
  CREATE_ORG_ADMIN: 'create_org_user',
  EDIT_ORG_ADMIN: 'edit_org_user',
  BLOCK_ORG_ADMIN: 'block_org_user',
  UNBLOCK_ORG_ADMIN: 'unblock_org_user',
  ADD_ORG_USER_ROLE: 'add_org_user_role',
  CREATE_BILLING_PLAN: 'create_payment_plan',
  UPDATE_BILLING_PLAN_STATUS: 'update_payment_plan_status',
  CREATE_ORGANIZATION_WITH_MASTER_ADMIN: 'create_organization_with_master_admin',
  CREATE_ORGANIZATION_WITH_MASTER_ADMIN_ADD_ROLE: 'create_organization_with_master_admin_add_role',
} as const;

export type PinTokenEventScopeType = ValueOf<typeof PIN_TOKEN_EVENT_SCOPE>;

export const USER_ASSIGNMENT_STATUS_CODE_MAP = {
  IS_NOT_ALLOWED_ROLE: 'is_not_allowed_role',
  IS_SAME_USER: 'is_same_user',
  IS_NOT_SAME_ROLE_AND_INVITED: 'is_not_same_role_and_invited',
  IS_SAME_ROLE_AND_INVITED_BUT_CREATED_BY_SAME_ORGANIZATION:
    'is_same_role_and_invited_but_created_by_same_organization',
  IS_SAME_ROLE_AND_INVITED_BUT_CREATED_BY_ANOTHER_ORGANIZATION:
    'is_same_role_and_invited_but_created_by_another_organization',
  CAN_BE_ASSIGNED: 'can_be_assigned',
} as const;

export type UserAssignmentStatusCodeType = ValueOf<typeof USER_ASSIGNMENT_STATUS_CODE_MAP>;

export const UPDATE_USER_MOBILE_PHONE_NOTIFICATION = (
  <span>
    The phone number associated with the invitation has been updated. <br /> The initial email invitation is still
    valid.
  </span>
);

export const UPDATE_USER_ROLE_NOTIFICATION = <span>The role of organization user has been updated.</span>;

export const TOKEN_REFRESHED_EVENT = 'TOKEN_REFRESHED';

export const RESEND_INVITATION_NOTIFICATION = (
  <Box display='flex' flexDirection='column' rowGap='12px'>
    <NotificationMessage>
      Please note that invitation emails may take some time to reach users. Our system relies on the third-party email
      service for delivery, and processing time can vary.
    </NotificationMessage>
    <NotificationMessage>
      If a user does not receive the invitation within a few minutes, ask them to check their spam or junk folder. If
      the email still hasn't arrived after some time, you may resend the invitation or contact support for assistance.
    </NotificationMessage>
  </Box>
);

export const CONTACT_MOBILE_PHONE = '+18886686221';
export const FORMATTED_CONTACT_MOBILE_PHONE = '+1 (888) 668-6221';

export const BILLING_FREQUENCY_DB = {
  MONTH: 'month',
  YEAR: 'year',
} as const;

export type BillingFrequencyType = ValueOf<typeof BILLING_FREQUENCY_DB>;

export const BILLING_FREQUENCY_LABEL = {
  [BILLING_FREQUENCY_DB.MONTH]: 'monthly',
  [BILLING_FREQUENCY_DB.YEAR]: 'yearly',
};

// Admin with <standard> plan - require billing subscription
// Admin with <enterprise> plan - does not require billing subscription (billed manually w/o subscription)
export const SUBSCRIPTION_PLAN_TYPE = {
  STANDARD: 'standard',
  ENTERPRISE: 'enterprise',
} as const;

export type SubscriptionPlanType = ValueOf<typeof SUBSCRIPTION_PLAN_TYPE>;

/*
  Active - subscription is paid
  Grace - payment renewal could not be processed (3 days of trial before suspend)
  Suspended - payment failed
  Cancelled - subscription is cancelled
*/
export const BILLING_SUBSCRIPTION_STATUS = {
  SUSPENDED: 'suspended',
  CANCELLED: 'canceled',
  GRACE: 'grace_period',
  ACTIVE: 'active',
} as const;

export type BillingSubscriptionStatusType = ValueOf<typeof BILLING_SUBSCRIPTION_STATUS>;

export const BILLING_PLAN_SCHEDULE_TYPE = {
  RECURRING: 'recurring',
  ONE_TIME: 'one-time',
} as const;

export type BillingPlanScheduleType = ValueOf<typeof BILLING_PLAN_SCHEDULE_TYPE>;

export const MASTER_ADMIN_URL_PREFIX = '/master-admin';
export const STRIPE_LINK = 'https://stripe.com';
export const UNLIMITED_USERS_LIMIT = -1;
export const INFINITY_SIGN = '∞';
export const SUBSCRIPTION_GRACE_PERIOD_DAYS = 3;

export const ORGANIZATION_USER_RESTRICTED_ACTION_ERROR_MESSAGE = 'Organization must contain at least one master admin';
export const UNEXPECTED_ERROR_MESSAGE = 'Something went wrong. Please reload the page and try again.';
export const PERSONAL_INFORMATION_UPDATED_MESSAGE = 'Personal information has been updated.';

export const API_KEY_STATUS_TYPE = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
} as const;

export type ApiKeyStatusType = ValueOf<typeof API_KEY_STATUS_TYPE>;

// Master Admin while onboarding have 3 state values for onboarding stepper:
export const SIGN_UP_STEP = {
  ONBOARDING: 'onboarding', // started onboarding via sign up
  TRIAL_MODE: 'trial_mode', // set password and can be logged into app in trial mode
  COMPLETED: 'completed', // purchased paid subscription
} as const;

export type SignUpStepType = ValueOf<typeof SIGN_UP_STEP>;
